import { useEffect, useMemo, useState } from "react";

import { useSearchAdminPermissions } from "@/search/Langston/useSearchAdminPermissions";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { useStandardsSearchContext } from "@/search/Langston/StandardsSearchProvider";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { useConceptTaxonomyProvider } from "@/search/Langston/ConceptTaxonomyProvider";
import {
  MAX_FILTERS_DISPLAY_COLLAPSED,
  NO_CLEAR_FILTERS,
  NO_SHOW_FILTERS,
  GENERATION_GENIUS_VIDEO_PARAM,
  MATURITY_FILTER_VALUE,
} from "../FilterPanel/FilterControls/FilterControls.constants";

export const useFilterControls = () => {
  const [showAllSelectedFilters, setShowAllSelectedFilters] = useState(false);
  const [allowCollapseFilters, setAllowCollapseFilters] = useState(false);
  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();
  const { setSelectedStandards } = useStandardsSearchContext();
  const {
    selectedIds: selectedTaxonomyTags,
    setSelectedIds: setSelectedTaxonomyTags,
  } = useConceptTaxonomyProvider();

  const {
    handlers: {
      getAllSearchParams,
      toggleSearchParamValue,
      deleteSearchParamValues,
      deleteSingleParamValue,
    },
  } = useSearchPageContext();

  const allSearchParams = getAllSearchParams();
  const baseChipsToDisplay = allSearchParams
    .filter(([paramName, paramValue]: [string, string]) => {
      // Only display "Spanish" chip when selected.
      if (paramName === LangstonParameterSchema.languages.key) {
        return paramValue !== "LANG_EN";
      } else {
        return true;
      }
    })
    .filter(([paramName]: [string]) => !NO_SHOW_FILTERS.includes(paramName))
    .reduce(
      (
        acc: [string[], string[]],
        [paramName, paramValue]: [string, string],
      ) => {
        if (paramValue) {
          acc.push([paramName, paramValue]);
        }
        return acc;
      },
      [],
    );

  // We want to give GG content more visibility; if GG content type is selected,
  // let's include it as a filter chip in our filter controls.
  const hasGenerationGeniusContentTypeSelected = allSearchParams.some(
    ([paramName, paramValue]: [string, string]) => {
      if (paramName !== LangstonParameterSchema.content_types.key) return false;

      return paramValue.includes(GENERATION_GENIUS_VIDEO_PARAM);
    },
  );

  const filterChipsToDisplay = hasGenerationGeniusContentTypeSelected
    ? [
        ...baseChipsToDisplay,
        [
          LangstonParameterSchema.content_types.key,
          GENERATION_GENIUS_VIDEO_PARAM,
        ],
      ]
    : baseChipsToDisplay;

  const handleClearAllFilters = () => {
    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    const hasTaxonomyTagsFilter = allSearchParams
      .flat()
      .find(
        (filterParamValue: string) =>
          filterParamValue === LangstonParameterSchema.topics.key,
      );

    if (hasTaxonomyTagsFilter) {
      setSelectedTaxonomyTags(new Set());
      deleteSearchParamValues(LangstonParameterSchema.topics.key);
    }

    allSearchParams.forEach(([paramName]: [string]) => {
      if (NO_CLEAR_FILTERS.includes(paramName)) return;
      if (paramName === LangstonParameterSchema.standards.key)
        setSelectedStandards([]);
      deleteSearchParamValues(paramName);
    });
  };

  const handleRemoveMaturityFilter = () => {
    deleteSingleParamValue(LangstonParameterSchema.minimum_maturity.key);
    deleteSingleParamValue(LangstonParameterSchema.maximum_maturity.key);
  };

  const handleRemoveFilter = (_: unknown, value: string) => {
    if (value === MATURITY_FILTER_VALUE) {
      handleRemoveMaturityFilter();
      return;
    }

    const [paramName, paramValue] = value.split(",") as [
      paramName: string,
      paramValue: string,
    ];

    if (paramName === LangstonParameterSchema.standards.key) {
      setSelectedStandards((prev) =>
        prev.filter((standard) => standard.id !== paramValue),
      );
    }

    if (paramName === LangstonParameterSchema.topics.key) {
      const updatedSelectedTaxonomyTags = [...selectedTaxonomyTags].filter(
        (id: string) => {
          return id !== paramValue;
        },
      );

      setSelectedTaxonomyTags(new Set(updatedSelectedTaxonomyTags));
      deleteSingleParamValue(LangstonParameterSchema.topics.key, paramValue);

      if (updatedSelectedTaxonomyTags.length === 0) {
        deleteSearchParamValues(LangstonParameterSchema.topics.key);
      }
    } else {
      toggleSearchParamValue(paramName, paramValue);
    }
  };

  const handleToggleShowFilters = () => {
    setShowAllSelectedFilters(!showAllSelectedFilters);
  };

  const filteredChipsToDisplay = useMemo(() => {
    return showAllSelectedFilters
      ? filterChipsToDisplay
      : filterChipsToDisplay.slice(0, MAX_FILTERS_DISPLAY_COLLAPSED);
  }, [showAllSelectedFilters, filterChipsToDisplay]);

  useEffect(() => {
    if (
      filterChipsToDisplay.length > MAX_FILTERS_DISPLAY_COLLAPSED &&
      !allowCollapseFilters
    ) {
      setAllowCollapseFilters(true);
    } else if (
      filterChipsToDisplay.length <= MAX_FILTERS_DISPLAY_COLLAPSED &&
      allowCollapseFilters
    ) {
      setAllowCollapseFilters(false);
    }
  }, [filteredChipsToDisplay]);

  return {
    allowCollapseFilters,
    filteredChipsToDisplay,
    showAllSelectedFilters,
    handleClearAllFilters,
    handleRemoveFilter,
    handleToggleShowFilters,
    MATURITY_FILTER_VALUE,
  };
};
